import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Chip, Typography, Paper, Collapse, IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// FEAT-010 / Phase C — Matrix 관점 3 "프로젝트 × 기능".
// Project 별 그룹 (projects-meta status/owner) → 묶인 Feature → 각 Feature 의 SW chip + derived Product.
// read-only — 편집은 /features. 새 관계 없이 derive 체인 시각화.

const UNASSIGNED = '__unassigned__';

const PROJECT_STATUS_COLOR = {
  plan: 'info', wip: 'warning', done: 'success', archive: 'default',
};
const PROJECT_STATUS_ICON = { plan: '📝', wip: '🛠', done: '✅', archive: '📦' };
const FEATURE_STATUS_COLOR = {
  planned: 'info', 'in-development': 'warning', ga: 'success', maintenance: 'info', eol: 'error',
};

// derived Product — feature 의 components[].product(=SW id) 가 속한 Product (products.softwares 역참조)
function derivedProducts(feature, products) {
  const swSet = new Set((feature.components || []).map((c) => c?.product).filter(Boolean));
  if (swSet.size === 0) return [];
  return products
    .filter((p) => (p.softwares || []).some((sw) => swSet.has(sw)))
    .map((p) => p.id);
}

export default function ProjectFeatureMatrix({
  projects = [],
  features = [],
  products = [],
  search = '',
  selectedStatuses = [],
  initialProject = '',
}) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});

  // Project slug → meta
  const projectMeta = useMemo(() => {
    const m = new Map();
    for (const p of projects) m.set(p.slug, p);
    return m;
  }, [projects]);

  // Feature 를 project 별로 그룹핑 (projects-meta 순서 + orphan + 미배정)
  const groups = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    const byProject = new Map();
    for (const f of features) {
      // 검색 / status 필터
      if (q && !`${f.id} ${f.name || ''} ${f.project || ''}`.toLowerCase().includes(q)) continue;
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(f.status)) continue;
      const key = f.project || UNASSIGNED;
      if (!byProject.has(key)) byProject.set(key, []);
      byProject.get(key).push(f);
    }
    // 순서: projects-meta 순서 우선 → orphan slug → 미배정
    const ordered = [];
    for (const p of projects) {
      if (initialProject && p.slug !== initialProject) continue;
      ordered.push({ slug: p.slug, meta: p, features: byProject.get(p.slug) || [] });
      byProject.delete(p.slug);
    }
    // orphan (projects-meta 에 없는 slug)
    for (const [key, feats] of byProject) {
      if (key === UNASSIGNED) continue;
      if (initialProject && key !== initialProject) continue;
      ordered.push({ slug: key, meta: null, features: feats });
    }
    // 미배정
    if (!initialProject && byProject.has(UNASSIGNED)) {
      ordered.push({ slug: UNASSIGNED, meta: null, features: byProject.get(UNASSIGNED) });
    }
    return ordered;
  }, [features, projects, search, selectedStatuses, initialProject]);

  const toggle = (slug) => setCollapsed((prev) => ({ ...prev, [slug]: !prev[slug] }));

  if (groups.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          표시할 Project 가 없습니다{initialProject ? ` (필터: ${initialProject})` : ''}.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.5}>
      {groups.map(({ slug, meta, features: feats }) => {
        const isUnassigned = slug === UNASSIGNED;
        const isCol = collapsed[slug];
        const statusColor = meta ? (PROJECT_STATUS_COLOR[meta.status] || 'default') : 'default';
        return (
          <Paper key={slug} variant="outlined" sx={{ overflow: 'hidden' }}>
            {/* Project 헤더 */}
            <Box
              onClick={() => toggle(slug)}
              sx={{
                px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: 'background.default', cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <IconButton size="small" sx={{ p: 0 }}>
                {isCol ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
              <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                {isUnassigned ? '(미배정)' : slug}
              </Typography>
              {meta && (
                <Chip
                  label={`${PROJECT_STATUS_ICON[meta.status] || ''} ${meta.status}`}
                  size="small"
                  color={statusColor}
                />
              )}
              {!meta && !isUnassigned && (
                <Chip label="projects-meta 없음" size="small" variant="outlined" sx={{ fontSize: 10 }} />
              )}
              {meta?.owner && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>👤 {meta.owner}</Typography>
              )}
              <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                {feats.length} features
              </Typography>
            </Box>

            {/* Feature 목록 */}
            <Collapse in={!isCol}>
              {feats.length === 0 ? (
                <Box sx={{ px: 4, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    묶인 Feature 가 없습니다
                  </Typography>
                </Box>
              ) : (
                feats.map((f) => {
                  const prods = derivedProducts(f, products);
                  const sws = (f.components || []).map((c) => c?.product).filter(Boolean);
                  return (
                    <Box
                      key={f.id}
                      onClick={() => navigate(`/features?focus=${encodeURIComponent(f.id)}`)}
                      sx={{
                        px: 2, py: 1.25, pl: 4, borderTop: 1, borderColor: 'divider',
                        display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 1.5,
                        alignItems: 'start', cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: 'secondary.main', fontSize: 11 }}>
                            {f.id}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.name}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>SW</Typography>
                          {sws.length > 0 ? sws.map((sw, idx) => (
                            <Chip key={`${sw}-${idx}`} label={sw} size="small" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }} />
                          )) : <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>없음</Typography>}
                          {prods.length > 0 && (
                            <>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'JetBrains Mono, monospace', fontSize: 10, ml: 0.5 }}>→ Product</Typography>
                              {prods.map((pr) => (
                                <Chip key={pr} label={pr} size="small" sx={{
                                  fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                                  bgcolor: 'secondary.dark', color: 'secondary.contrastText',
                                  border: '1px solid', borderColor: 'secondary.main',
                                }} />
                              ))}
                            </>
                          )}
                        </Stack>
                      </Box>
                      <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                        {f.status && (
                          <Chip label={f.status} size="small" color={FEATURE_STATUS_COLOR[f.status] || 'default'} />
                        )}
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>→ /features</Typography>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
}
